import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Input,
  Modal,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  HistoryOutlined,
  FileTextOutlined,
  LinkOutlined,
  BookOutlined,
  UndoOutlined,
  SyncOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useKeycloak } from '@react-keycloak/web';
import useTokenCount from '../../hooks/useTokenCount';
import { attachOverlayScrollbar, useOverlayScrollbarRef } from '../../hooks/useOverlayScrollbar';
import PromptTokenMeter from './PromptTokenMeter';
import PromptDiffView from './PromptDiffView';
import PromptProposalView from './PromptProposalView';
import PromptHistoryPanel from './PromptHistoryPanel';
import PromptImpactModal from './PromptImpactModal';
import PromptAssistPanel from './PromptAssistPanel';
import PromptAssistReviewBar from './PromptAssistReviewBar';
import SparkleIcon from './SparkleIcon';
import { resolveAssistAction } from './promptAssistActions';
import { getSelectionRect, positionToolbar } from './selectionRect';
import { formatDateShort } from '../../pages/channels/utils/formatters';
import {
  BASE_REF,
  buildBodyWithBase,
  deriveInheritanceState,
  lintPrompt,
  splitBaseBody,
} from './promptUtils';
import {
  resolvePromptBinding,
  createPromptVersion,
  listPromptVersions,
  publishPrompt,
  rollbackPrompt,
  getPromptBindings,
  assistPrompt,
} from '../../services/api/prompts';
import './PromptEditor.css';

const { Text } = Typography;
const { TextArea } = Input;

const DEFAULT_FEATURES = {
  history: true,
  assist: true,
  library: true,
  publish: true,
  linter: true,
  testPane: false,
  compareAnyTwo: true,
  rollback: true,
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const fadeVariants = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

const cardVariants = {
  initial: { opacity: 0, y: 8 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: prefersReducedMotion() ? 0 : i * 0.04, duration: 0.18, ease: 'easeOut' },
  }),
  exit: { opacity: 0, y: -4, transition: { duration: 0.12 } },
};

export default function PromptEditor({
  value,
  onChange,
  binding,
  basePrompt,
  models = [],
  features: featuresProp = {},
  placeholder = 'Write instructions for this assistant…',
  onPublished,
}) {
  const { keycloak } = useKeycloak();
  const features = { ...DEFAULT_FEATURES, ...featuresProp };
  const [promptMeta, setPromptMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStartCards, setShowStartCards] = useState(!value);
  const [collapsedBase, setCollapsedBase] = useState(false);
  const [chipExpanded, setChipExpanded] = useState(false);
  const [diverged, setDiverged] = useState(false);
  const [preDivergeBody, setPreDivergeBody] = useState(null);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionHighlightDiff, setVersionHighlightDiff] = useState(true);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [impactOpen, setImpactOpen] = useState(false);
  const [bindings, setBindings] = useState([]);
  const [publishLoading, setPublishLoading] = useState(false);
  const [assistPanelOpen, setAssistPanelOpen] = useState(false);
  const [assistPanelAnchor, setAssistPanelAnchor] = useState('footer');
  const [assistProposal, setAssistProposal] = useState(null);
  const [assistHighlightDiff, setAssistHighlightDiff] = useState(true);
  const [assistOp, setAssistOp] = useState(null);
  const [selection, setSelection] = useState(null);
  const [toolbarPos, setToolbarPos] = useState(null);
  const selectionAnchorPosRef = useRef(null);
  const selectionAssistRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef(null);
  const versionPreviewScrollRef = useOverlayScrollbarRef();
  const assistOverlayScrollRef = useOverlayScrollbarRef();
  const editorBodyRef = useRef(null);
  const footerRef = useRef(null);
  const lastSavedRef = useRef(null);
  const lastAssistOpRef = useRef(null);
  const lastAssistExtraRef = useRef({});
  const abortRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current?.resizableTextArea?.textArea || textareaRef.current;
    if (!el) return undefined;
    return attachOverlayScrollbar(el);
  }, [selectedVersionId, assistProposal, isFullscreen]);

  const baseText = basePrompt?.text || '';
  const baseVersion = basePrompt?.version;
  const modelIds = useMemo(() => models.filter(Boolean), [models]);
  const { estimates, loading: tokenLoading, heuristic } = useTokenCount(keycloak, value, modelIds);
  const linterNotes = features.linter ? lintPrompt(value) : [];

  const inheritanceState = useMemo(
    () => deriveInheritanceState(value, baseText),
    [value, baseText],
  );

  const displayMode = useMemo(() => {
    if (!baseText || !binding || binding.targetType === 'assistant') {
      return 'plain';
    }
    if (collapsedBase && inheritanceState === 'appending') return 'chip';
    if (inheritanceState === 'inherited' || inheritanceState === 'inherited_snapshot') return 'tinted';
    if (inheritanceState === 'appending' && !collapsedBase) return 'tinted';
    return 'plain';
  }, [baseText, binding, collapsedBase, inheritanceState]);

  const { usesBase, tail } = splitBaseBody(value);
  const editorDisplayValue = useMemo(() => {
    if (displayMode === 'chip') return tail;
    if (displayMode === 'tinted' && usesBase) return baseText + tail;
    return value || '';
  }, [displayMode, usesBase, baseText, tail, value]);

  const liveVersion = versions.find((v) => v.id === promptMeta?.live_version_id);
  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const hasUnpublishedChanges = useMemo(() => {
    if (!promptMeta?.id) return false;
    const liveBody = liveVersion?.body ?? '';
    return (value || '') !== liveBody;
  }, [promptMeta?.id, liveVersion?.body, value]);

  const loadBinding = useCallback(async () => {
    if (!binding || !keycloak?.token) return;
    setLoading(true);
    try {
      const data = await resolvePromptBinding(keycloak, {
        target_type: binding.targetType,
        target_id: binding.targetId,
        slot: binding.slot,
        name: binding.name,
        owner: binding.owner,
        group_id: binding.groupId,
        initial_body: value || '',
      });
      setPromptMeta(data.prompt);
      const body = data.draft_body ?? data.live_body ?? '';
      if (!value && body) onChange?.(body);
      if (body === BASE_REF || (usesBase && tail)) {
        setCollapsedBase(usesBase && !!tail.trim());
      }
      lastSavedRef.current = body;
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [binding, keycloak, value, onChange, usesBase, tail]);

  useEffect(() => {
    loadBinding();
  }, [binding?.targetType, binding?.targetId, binding?.slot]);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
    setHistoryPanelOpen(false);
    setSelectedVersionId(null);
    setVersionHighlightDiff(true);
  }, []);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') exitFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen, exitFullscreen]);

  const loadVersions = useCallback(async () => {
    if (!promptMeta?.id || !keycloak?.token) return;
    setVersionsLoading(true);
    try {
      const v = await listPromptVersions(keycloak, promptMeta.id, 'summary');
      setVersions(v);
    } finally {
      setVersionsLoading(false);
    }
  }, [promptMeta?.id, keycloak]);

  useEffect(() => {
    if (promptMeta?.id) loadVersions();
  }, [promptMeta?.id, loadVersions]);

  const openHistory = () => {
    setIsFullscreen(true);
    setHistoryPanelOpen(true);
    loadVersions();
  };

  const closeHistoryPanel = () => {
    setHistoryPanelOpen(false);
    setSelectedVersionId(null);
    setVersionHighlightDiff(true);
  };

  const handleStartBlank = () => {
    setShowStartCards(false);
    onChange?.('');
  };

  const handleUseAssistant = () => {
    setShowStartCards(false);
    setCollapsedBase(false);
    setDiverged(false);
    onChange?.(BASE_REF);
  };

  const handleEditorChange = (e) => {
    const newDisplay = e.target.value;
    setShowStartCards(false);

    if (assistPanelOpen) {
      setAssistPanelOpen(false);
      if (assistPanelAnchor === 'selection') {
        setSelection(null);
        setToolbarPos(null);
        selectionAnchorPosRef.current = null;
      }
    }

    if (displayMode === 'chip') {
      onChange?.(buildBodyWithBase(newDisplay));
      return;
    }

    if (displayMode === 'tinted' && baseText) {
      if (!newDisplay.startsWith(baseText)) {
        setDiverged(true);
        setPreDivergeBody(value);
        onChange?.(newDisplay);
        return;
      }
      const newTail = newDisplay.slice(baseText.length);
      if (newTail.length > 0 && !collapsedBase) {
        setCollapsedBase(true);
      }
      onChange?.(buildBodyWithBase(newTail.replace(/^\n+/, '')));
      return;
    }

    onChange?.(newDisplay);
  };

  const handleUndoDiverge = () => {
    if (preDivergeBody != null) {
      onChange?.(preDivergeBody);
      setDiverged(false);
      setPreDivergeBody(null);
    }
  };

  const handleResync = () => {
    Modal.confirm({
      title: 'Re-sync with assistant prompt?',
      content: (
        <div>
          <Text type="secondary">Your current text will be replaced. Review the diff:</Text>
          <div style={{ marginTop: 12, maxHeight: 200, overflow: 'auto' }}>
            <PromptDiffView oldText={value} newText={BASE_REF === value ? baseText : BASE_REF} />
          </div>
        </div>
      ),
      onOk: () => {
        setDiverged(false);
        setCollapsedBase(false);
        onChange?.(BASE_REF);
      },
    });
  };

  const handlePublishClick = async () => {
    if (!hasUnpublishedChanges) return;
    if (!promptMeta?.id) {
      onChange?.(value);
      return;
    }
    try {
      const b = await getPromptBindings(keycloak, promptMeta.id);
      setBindings(b);
    } catch {
      setBindings([]);
    }
    setImpactOpen(true);
  };

  const handlePublishConfirm = async () => {
    if (!promptMeta?.id) return;
    setPublishLoading(true);
    try {
      await createPromptVersion(keycloak, promptMeta.id, {
        body: value || '',
        status: 'draft',
      });
      await publishPrompt(keycloak, promptMeta.id, {});
      message.success('Published');
      setImpactOpen(false);
      onPublished?.();
      lastSavedRef.current = value || '';
      await loadBinding();
      await loadVersions();
    } catch (e) {
      message.error(e.message || 'Publish failed');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleRollback = async (versionId) => {
    try {
      await rollbackPrompt(keycloak, promptMeta.id, versionId);
      message.success('Rolled back');
      const v = versions.find((x) => x.id === versionId);
      if (v) {
        onChange?.(v.body);
        lastSavedRef.current = v.body;
      }
      setSelectedVersionId(null);
      await loadBinding();
      await loadVersions();
    } catch (e) {
      message.error(e.message || 'Rollback failed');
    }
  };

  const handleStopAssist = () => {
    abortRef.current?.abort();
    setAssistOp(null);
  };

  const openAssistPanel = (anchor = 'footer') => {
    setAssistPanelAnchor(anchor);
    setAssistPanelOpen(true);
    if (anchor === 'footer') {
      selectionAnchorPosRef.current = null;
    }
  };

  const closeAssistPanel = () => {
    setAssistPanelOpen(false);
    setAssistProposal(null);
    setAssistHighlightDiff(true);
    setToolbarPos(null);
    setSelection(null);
    selectionAnchorPosRef.current = null;
  };

  const dismissSelectionAssist = useCallback(() => {
    setAssistPanelOpen(false);
    setSelection(null);
    setToolbarPos(null);
    selectionAnchorPosRef.current = null;
  }, []);

  const runAssist = async (actionKey, extra = {}, keepPanelOpen = false) => {
    const { op, extra: actionExtra } = resolveAssistAction(actionKey);
    const mergedExtra = { ...actionExtra, ...extra };

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    lastAssistOpRef.current = actionKey;
    lastAssistExtraRef.current = mergedExtra;
    setAssistOp(actionKey);
    setAssistPanelOpen(true);
    if (!keepPanelOpen) {
      setAssistProposal(null);
      setAssistHighlightDiff(true);
    }
    try {
      const result = await assistPrompt(keycloak, {
        op,
        text: value || '',
        selection_start: selection?.start,
        selection_end: selection?.end,
        ...mergedExtra,
      }, controller.signal);
      if (!controller.signal.aborted) {
        setAssistProposal(result);
        setAssistHighlightDiff(true);
        setAssistPanelOpen(false);
        setSelection(null);
        setToolbarPos(null);
        selectionAnchorPosRef.current = null;
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        message.error(e.message || 'Assist failed');
      }
    } finally {
      setAssistOp(null);
    }
  };

  const handleAssistAction = (actionKey) => {
    if (assistPanelAnchor === 'selection' && toolbarPos) {
      selectionAnchorPosRef.current = toolbarPos;
    }
    runAssist(actionKey);
  };

  const handleAskAiSubmit = (instruction) => {
    if (!instruction?.trim()) return;
    runAssist('freeform', { instruction: instruction.trim() });
  };

  const handleTryAgain = () => {
    if (lastAssistOpRef.current) {
      runAssist(lastAssistOpRef.current, lastAssistExtraRef.current, true);
    }
  };

  const handleAcceptAssist = () => {
    if (assistProposal?.proposal) onChange?.(assistProposal.proposal);
    closeAssistPanel();
  };

  const handleDiscardAssist = () => {
    closeAssistPanel();
  };

  const handleSelectVersion = (versionId) => {
    setSelectedVersionId(versionId);
    setVersionHighlightDiff(true);
  };

  const handleCompareVersion = (versionId) => {
    setSelectedVersionId(versionId);
    setVersionHighlightDiff(true);
  };

  const handleSelect = () => {
    const el = textareaRef.current?.resizableTextArea?.textArea || textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start !== end && features.assist) {
      setSelection({ start, end });
      setAssistPanelAnchor('selection');
      openAssistPanel('selection');
      const rect = getSelectionRect(el, start, end);
      if (rect && editorBodyRef.current) {
        const containerRect = editorBodyRef.current.getBoundingClientRect();
        const pos = positionToolbar(rect, 320, 36, containerRect);
        setToolbarPos(pos);
        selectionAnchorPosRef.current = pos;
      }
    } else {
      if (assistPanelOpen && assistPanelAnchor === 'selection') {
        dismissSelectionAssist();
      } else {
        setSelection(null);
        setToolbarPos(null);
      }
    }
  };

  const renderMirrorBackdrop = () => {
    if (displayMode !== 'tinted' || !baseText) return null;
    const tintPart = baseText;
    const rest = editorDisplayValue.slice(baseText.length);
    return (
      <div className="prompt-mirror-backdrop" aria-hidden>
        <span className="prompt-base-tint">{tintPart}</span>
        <span style={{ color: 'var(--color-text)' }}>{rest}</span>
      </div>
    );
  };

  const renderVersionPreview = () => {
    if (!selectedVersion) return null;
    const versionBody = selectedVersion.body || '';
    const currentBody = value || '';

    return (
      <div className="prompt-version-preview" ref={versionPreviewScrollRef}>
        {versionHighlightDiff ? (
          <PromptDiffView oldText={versionBody} newText={currentBody} />
        ) : (
          <PromptProposalView oldText={currentBody} newText={versionBody} />
        )}
      </div>
    );
  };

  const renderAssistOverlay = () => {
    if (!assistProposal) return null;

    return (
      <div className="prompt-assist-editor-overlay" ref={assistOverlayScrollRef}>
        {assistHighlightDiff ? (
          <PromptDiffView
            oldText={assistProposal.original}
            newText={assistProposal.proposal}
            parts={assistProposal.diff}
          />
        ) : (
          <PromptProposalView
            oldText={assistProposal.original}
            newText={assistProposal.proposal}
            parts={assistProposal.diff}
          />
        )}
      </div>
    );
  };

  const showAssistReview = !selectedVersionId && !!assistProposal;

  const assistPanelProps = {
    assistOp,
    onAction: handleAssistAction,
    onAskSubmit: handleAskAiSubmit,
    onStop: handleStopAssist,
    variant: assistPanelAnchor,
  };

  const draftVersion = versions.find(
    (v) => v.status === 'draft' && v.id !== promptMeta?.live_version_id,
  );
  const viewingVersion = selectedVersion;
  const isViewingCurrentDraft = historyPanelOpen && !selectedVersionId;

  const historyContextVersion = viewingVersion || draftVersion || liveVersion;

  const selectionAssistPos = toolbarPos || selectionAnchorPosRef.current;

  const showSelectionPanel = features.assist
    && assistPanelOpen
    && assistPanelAnchor === 'selection'
    && !assistProposal
    && selectionAssistPos;

  useEffect(() => {
    if (!showSelectionPanel) return undefined;

    const onPointerDown = (e) => {
      if (selectionAssistRef.current?.contains(e.target)) return;
      dismissSelectionAssist();
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [showSelectionPanel, dismissSelectionAssist]);

  const startCards = [
    {
      key: 'blank',
      icon: <FileTextOutlined className="prompt-start-card-icon" />,
      title: 'Start blank',
      desc: 'Write from scratch',
      onClick: handleStartBlank,
    },
    basePrompt && binding?.targetType !== 'assistant' && {
      key: 'assistant',
      icon: <LinkOutlined className="prompt-start-card-icon" />,
      title: "Use assistant's prompt",
      desc: baseVersion ? `Inherit · v${baseVersion}` : 'Inherit and stay in sync',
      onClick: handleUseAssistant,
    },
    features.library && {
      key: 'library',
      icon: <BookOutlined className="prompt-start-card-icon" />,
      title: 'From library',
      desc: 'Use a saved template',
      onClick: () => message.info('Open the Prompt Library to insert a template'),
    },
  ].filter(Boolean);

  const editorContent = (
    <div
      className={[
        'prompt-editor',
        isFullscreen ? 'is-fullscreen' : '',
        historyPanelOpen ? 'is-history-mode' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="prompt-editor-layout">
        <div className={`prompt-editor-main${isFullscreen ? ' is-fullscreen-main' : ''}`}>
          <div className="prompt-editor-chrome">
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Expand editor'} placement="left">
              <Button
                type="text"
                size="small"
                className="prompt-chrome-btn"
                icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={() => (isFullscreen ? exitFullscreen() : setIsFullscreen(true))}
              />
            </Tooltip>
            {isFullscreen && features.history && promptMeta && (
              <Tooltip title={historyPanelOpen ? 'Hide version history' : 'Show version history'} placement="left">
                <Button
                  type="text"
                  size="small"
                  className={`prompt-chrome-btn${historyPanelOpen ? ' prompt-chrome-btn-active' : ''}`}
                  icon={<HistoryOutlined />}
                  onClick={() => setHistoryPanelOpen(!historyPanelOpen)}
                />
              </Tooltip>
            )}
          </div>

          <AnimatePresence mode="wait">
            {showStartCards && !value?.trim() && !loading && !selectedVersionId && (
              <motion.div
                key="start-cards"
                className="prompt-editor-start-cards"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={fadeVariants}
                transition={{ duration: prefersReducedMotion() ? 0 : 0.18 }}
              >
                {startCards.map((card, i) => (
                  <motion.button
                    key={card.key}
                    type="button"
                    className="prompt-start-card"
                    onClick={card.onClick}
                    custom={i}
                    variants={cardVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    {card.icon}
                    <div className="prompt-start-card-title">{card.title}</div>
                    <div className="prompt-start-card-desc">{card.desc}</div>
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {displayMode === 'chip' && !selectedVersionId && (
              <motion.div
                key="chip"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: prefersReducedMotion() ? 0 : 0.18 }}
              >
                <div
                  className="prompt-base-chip"
                  onClick={() => setChipExpanded(!chipExpanded)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setChipExpanded(!chipExpanded)}
                >
                  <LinkOutlined />
                  <span>
                    Assistant base prompt
                    {baseVersion ? ` · v${baseVersion}` : ''}
                  </span>
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setCollapsedBase(false); }}
                  >
                    Expand
                  </Button>
                </div>
                {chipExpanded && (
                  <div className="prompt-base-chip-preview">{baseText}</div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {displayMode === 'tinted' && baseText && !selectedVersionId && (
            <div className="prompt-inherited-label">
              From assistant{baseVersion ? ` · v${baseVersion}` : ''}
            </div>
          )}

          <AnimatePresence>
            {historyPanelOpen && (
              <motion.div
                key="version-context"
                className="prompt-version-context-bar"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: prefersReducedMotion() ? 0 : 0.22, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="prompt-version-context-main">
                  <span className="prompt-version-context-title">
                    {viewingVersion
                      ? `v${viewingVersion.version_number}`
                      : 'Current draft'}
                  </span>
                  {historyContextVersion?.created_at && (
                    <span className="prompt-version-context-date">
                      {formatDateShort(historyContextVersion.created_at)}
                    </span>
                  )}
                  {viewingVersion && !isViewingCurrentDraft && (
                    <Button
                      type="link"
                      size="small"
                      className="prompt-version-context-back"
                      onClick={() => {
                        setSelectedVersionId(null);
                        setVersionHighlightDiff(true);
                      }}
                    >
                      View current draft
                    </Button>
                  )}
                </div>
                {viewingVersion && (
                  <label className="prompt-version-highlight-label">
                    <input
                      type="checkbox"
                      checked={versionHighlightDiff}
                      onChange={(e) => setVersionHighlightDiff(e.target.checked)}
                    />
                    Highlight differences
                  </label>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="prompt-editor-body" ref={editorBodyRef}>
            <div className="prompt-mirror-wrap">
              {!selectedVersionId && !showAssistReview && renderMirrorBackdrop()}
              {selectedVersionId ? (
                renderVersionPreview()
              ) : showAssistReview ? (
                renderAssistOverlay()
              ) : (
                <TextArea
                  ref={textareaRef}
                  className="prompt-mirror-textarea"
                  value={editorDisplayValue}
                  onChange={handleEditorChange}
                  onSelect={handleSelect}
                  onKeyDown={() => setShowStartCards(false)}
                  placeholder={showStartCards ? '' : placeholder}
                  autoSize={isFullscreen ? false : { minRows: 6, maxRows: 24 }}
                  disabled={loading}
                />
              )}
            </div>

            <PromptAssistReviewBar
              visible={!!assistProposal}
              highlightDiff={assistHighlightDiff}
              onHighlightDiffChange={setAssistHighlightDiff}
              onDiscard={handleDiscardAssist}
              onTryAgain={handleTryAgain}
              onAccept={handleAcceptAssist}
              canAccept={!!assistProposal?.proposal}
              loading={assistOp != null}
            />

            <div className="prompt-editor-footer" ref={footerRef}>
              <div className="prompt-editor-footer-left">
                <PromptTokenMeter estimates={estimates} heuristic={heuristic} loading={tokenLoading} />
                {diverged && !selectedVersionId && (
                  <span className="prompt-editor-diverged-inline">
                    Edited — no longer follows assistant ·{' '}
                    <Button type="link" size="small" icon={<UndoOutlined />} onClick={handleUndoDiverge}>
                      Undo
                    </Button>
                    {' · '}
                    <Button type="link" size="small" icon={<SyncOutlined />} onClick={handleResync}>
                      Re-sync
                    </Button>
                  </span>
                )}
              </div>
              <div className="prompt-editor-footer-right">
                <span className="prompt-editor-status">
                  <AnimatePresence mode="wait">
                    {hasUnpublishedChanges && (
                      <motion.span
                        key="draft-label"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion() ? 0 : 0.15 }}
                      >
                        Draft
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {liveVersion ? `${hasUnpublishedChanges ? ' · ' : ''}Live v${liveVersion.version_number}` : ''}
                </span>
                {features.assist && !selectedVersionId && !assistProposal && (
                  <Tooltip title="AI assist">
                    <Button
                      type="text"
                      size="small"
                      className={assistPanelOpen && assistPanelAnchor === 'footer' ? 'prompt-chrome-btn-active' : ''}
                      icon={<SparkleIcon size={16} />}
                      onClick={() => {
                        if (assistPanelOpen && assistPanelAnchor === 'footer') {
                          closeAssistPanel();
                        } else {
                          openAssistPanel('footer');
                        }
                      }}
                    />
                  </Tooltip>
                )}
                {features.history && promptMeta && !isFullscreen && (
                  <Tooltip title="Version history">
                    <Button type="text" size="small" icon={<HistoryOutlined />} onClick={openHistory} />
                  </Tooltip>
                )}
                <AnimatePresence>
                  {features.publish && promptMeta && hasUnpublishedChanges && !selectedVersionId && (
                    <motion.div
                      key="publish-btn"
                      className="prompt-publish-wrap"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: prefersReducedMotion() ? 0 : 0.16, ease: 'easeOut' }}
                    >
                      <Button
                        type="primary"
                        size="small"
                        disabled={publishLoading}
                        loading={publishLoading}
                        onClick={handlePublishClick}
                      >
                        Publish
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <AnimatePresence>
              {assistPanelOpen && assistPanelAnchor === 'footer' && features.assist && !assistProposal && (
                <motion.div
                  key="footer-assist"
                  className="prompt-assist-panel-anchor prompt-assist-panel-anchor--footer"
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: prefersReducedMotion() ? 0 : 0.2, ease: [0.4, 0, 0.2, 1] }}
                >
                  <PromptAssistPanel
                    {...assistPanelProps}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showSelectionPanel && (
                <motion.div
                  ref={selectionAssistRef}
                  key="selection-assist"
                  className="prompt-assist-selection-wrap"
                  style={{
                    position: 'absolute',
                    top: selectionAssistPos?.top ?? 0,
                    left: selectionAssistPos?.left ?? 0,
                    zIndex: 20,
                  }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 28,
                    duration: prefersReducedMotion() ? 0 : undefined,
                  }}
                >
                  <PromptAssistPanel
                    {...assistPanelProps}
                    onMouseDown={(e) => e.preventDefault()}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {linterNotes.length > 0 && !selectedVersionId && (
            <div className="prompt-linter-notes">
              {linterNotes.map((n, i) => (
                <div key={i} className="prompt-linter-note">{n.message}</div>
              ))}
            </div>
          )}
        </div>

        <AnimatePresence>
        {historyPanelOpen && (
          <motion.div
            key="history-panel"
            className="prompt-history-panel-wrap"
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{
              duration: prefersReducedMotion() ? 0 : 0.28,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <PromptHistoryPanel
              versions={versions}
              loading={versionsLoading}
              onClose={closeHistoryPanel}
              liveVersionId={promptMeta?.live_version_id}
              selectedVersionId={selectedVersionId}
              onSelectVersion={handleSelectVersion}
              onCompare={handleCompareVersion}
              onRollback={features.rollback ? handleRollback : null}
            />
          </motion.div>
        )}
        </AnimatePresence>
      </div>

      <PromptImpactModal
        open={impactOpen}
        onCancel={() => setImpactOpen(false)}
        onConfirm={handlePublishConfirm}
        bindings={bindings}
        oldText={liveVersion?.body || ''}
        newText={value || ''}
        confirmLoading={publishLoading}
      />
    </div>
  );

  if (isFullscreen) {
    return createPortal(editorContent, document.body);
  }

  return editorContent;
}
